<template>
  <div class="flex flex-col">
    <div
      class="flex flex-col items-center overflow-auto rounded-lg border border-gray-800"
    >
      <RadioDrawer
        v-bind="subscriptionTypes[0]"
        rounded-class="rounded-t-lg"
        :model-value="subscriptionType"
        @update:model-value="onSelectType"
      >
        <template #badge>
          <div
            v-if="subscriptionDiscounts && subscriptionDiscounts.subsequent"
            class="text-md ml-2 w-fit rounded-t-lg bg-[#FDDA25] pl-3 pr-4 pt-1 font-medium"
            :class="{ 'opacity-50': disableAddToCart }"
          >
            {{
              $t('subscription.discount_badge', {
                discount:
                  subscriptionDiscounts.subsequent.value +
                  subscriptionDiscounts.subsequent.unit,
              })
            }}
          </div>
        </template>
        <template #default="drawer">
          <template v-if="!disableAddToCart && drawer.selected">
            <ProductSubscriptionSelection
              :product="product"
              :variants="variants"
              :quantity="quantity"
              :values="values"
              :disabled="!drawer.selected"
              :preferred-delivery-date="preferredDeliveryDate"
              :price-promotion-key="pricePromotionKey"
              @add-item-to-basket="$emit('addItemToBasket', $event)"
              @change="selectSubscription"
            >
              <template #tax-extras
                ><span class="text-sm text-gray-600"
                  ><slot name="tax-extras" :subscription="true"></slot></span
              ></template>
            </ProductSubscriptionSelection>
          </template>
        </template>
        <template #info-modal>
          <FimButton
            type="raw"
            no-padding
            is-small
            :disabled="disableAddToCart"
            @mousedown="showOverlay()"
          >
            <template #icon>
              <LazyIconFielmannInfoCircle
                class="size-4 text-shade-500 hover:text-shade-400"
              />
            </template> </FimButton
        ></template>
        <template #price>
          <slot name="price" :price="subscriptionPrice"></slot>
        </template>
        <template #description>
          <ProductSubscriptionSavingInfo
            class="ml-2 mt-2"
            :quantity="quantity"
            :has-initial-discount="hasInitialDiscount"
            :subsequent-price="subsequentPrice"
            :subscription-discounts="subscriptionDiscounts"
          />
        </template>
      </RadioDrawer>
      <RadioDrawer
        v-bind="subscriptionTypes[1]"
        :model-value="subscriptionType"
        rounded-class="rounded-b-lg"
        :disabled="disableAddToCart"
        @update:model-value="onSelectType"
      >
        <template #price>
          <slot name="price" :price="originalPrice"></slot>
        </template>
        <template #default="drawer">
          <span
            class="pt-3 text-sm text-gray-600"
            :class="{ hidden: !drawer.selected }"
          >
            <slot name="tax-extras" :subscription="false"></slot>
          </span>
        </template>
      </RadioDrawer>
    </div>
    <div class="flex w-full flex-col gap-6 py-4">
      <slot name="delivery-estimates"></slot>
      <FimButton
        data-test-id="add-to-cart"
        class="w-full"
        type="primary"
        :disabled="disableAddToCart"
        :title="product.isSoldOut ? $t('badge_labels.sold_out') : ''"
        @click="onAddItemToBasket"
      >
        {{ $t('pdp.add_label') }}
        <template #iconAfter>
          <LazyIconFielmannCart class="mr-2 size-6" />
        </template>
      </FimButton>
      <slot name="recommendations"></slot>
    </div>
    <Overlay
      v-if="isOverlayAvailable"
      :title="overlayTitle"
      :open="isOverlayOpen"
      portal-target="#info-overlay"
      layout="right"
      enter-from="right"
      @update:open="isOverlayOpen = false"
    >
      <div v-for="content in overlayContent" :key="content._uid">
        <component :is="content.component" :blok="content" />
      </div>
    </Overlay>
  </div>
</template>
<script setup lang="ts">
import { toRefs } from 'vue'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useSubscription } from '../composables/useSubscription'
import type { PreferredDeliveryDate } from '../helpers/subscription'
import type { ValuesType } from 'utility-types'
import { useOverlay } from '~/composables/useOverlay'

type Props = {
  product: Product
  variants?: Array<Variant | undefined>
  preferredDeliveryDate?: Array<PreferredDeliveryDate>
  pricePromotionKey?: string
  quantity?: number
  values?: SubscriptionDefinition
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  preferredDeliveryDate: () => getSubscriptionDeliveryDates(),
  pricePromotionKey: 'subscription',
  variants: undefined,
  quantity: 1,
  values: undefined,
  disabled: false,
})

const emit = defineEmits(['addItemToBasket'])

const { $i18n, $currentShop } = useNuxtApp()

const { trackRadioButtonClick } = useTrackingEvents()

const { product, variants, pricePromotionKey, disabled } = toRefs(props)
const subscription = await useSubscription(
  product,
  pricePromotionKey,
  variants,
  `product-subscription-${props.product.id}`,
)
const {
  subscriptionDiscounts,
  basketItemSubscriptionAttributes,
  subscriptionPrice,
  subsequentPrice,
  originalPrice,
  subscriptionVariantsEligible,
} = subscription

onBeforeMount(() => {
  if (props.values?.subscriptionInterval) {
    selectSubscription()
  }
})

const SubscriptionType = {
  ONE_TIME: 1,
  SUBSCRIBE: 2,
} as const

type SubscriptionType = ValuesType<typeof SubscriptionType>

const subscriptionTypes = [
  {
    value: SubscriptionType.SUBSCRIBE,
    label: subscriptionDiscounts.value.initial
      ? $i18n.t('subscription.subscribe_with_reduction', {
          percentage: subscriptionDiscounts.value.initial.value,
        })
      : $i18n.t('subscription.subscribe'),
  },
  {
    value: SubscriptionType.ONE_TIME,
    label: $i18n.t('subscription.one_time_purchase'),
  },
]
const defaultSubscriptionType = () => {
  if (!$currentShop.subscriptionPreselected) {
    return subscriptionTypes[1].value
  }
  // @todo brand === 'atrea' is a proxy for if(firstPrice != subsequentPrice){preselect one-time}
  if (getFirstAttributeLabel(props.product?.attributes, 'brand') === 'Atrea') {
    return subscriptionTypes[1].value
  }
  if (subscriptionVariantsEligible.value) {
    return subscriptionTypes[0].value
  }
  return subscriptionTypes[1].value
}

const subscriptionType = ref<number>(defaultSubscriptionType())

const hasManuallySelectedSubscription = ref(false)

const onSelectType = (type: SubscriptionType) => {
  subscriptionType.value = type
  trackRadioButtonClick(
    type === SubscriptionType.ONE_TIME
      ? { id: 'radio_oneTimePurchase', label: 'Einmalkauf' }
      : { id: 'radio_subscription', label: 'Spar-Abo' },
  )
  hasManuallySelectedSubscription.value = true
}

watch(subscriptionVariantsEligible, (isEligible, wasEligible) => {
  if (wasEligible && !isEligible) {
    // eligible > not eligible > select one time
    subscriptionType.value = SubscriptionType.ONE_TIME
  } else if (!hasManuallySelectedSubscription.value) {
    // if not manually selected, default to subscription if eligible
    subscriptionType.value = isEligible
      ? SubscriptionType.SUBSCRIBE
      : SubscriptionType.ONE_TIME
  }
})

const selectSubscription = () => {
  subscriptionType.value = SubscriptionType.SUBSCRIBE
}

const onAddItemToBasket = () => {
  if (subscriptionType.value === SubscriptionType.ONE_TIME) {
    return emit('addItemToBasket')
  }

  return emit('addItemToBasket', basketItemSubscriptionAttributes)
}

const disableAddToCart = computed(
  () =>
    disabled.value ||
    !isProductAvailable(product) ||
    !variants.value ||
    !areVariantsAvailable(product, variants.value),
)

const hasInitialDiscount = computed(
  () => subsequentPrice.value === subscriptionPrice.value?.withTax,
)

const {
  overlayTitle,
  overlayContent,
  available: isOverlayAvailable,
  open: isOverlayOpen,
  show: showOverlay,
} = await useOverlay(SubscriptionOverlays.INFO)
</script>
